import { ONBOARDING_THREAD_ID } from '@/utils/constants';

export const onboardingMessagesEN = {
  'step-0': {
    messages: {
      role: 'ai',
      content: [],
    },
  },
  'step-1': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-0',
          timestamp: '2024-11-21T06:35:39.224335+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content:
            'Which drivers need support due to an overall rating below 70 on average in the last month and list their ratings?',
        },
        {
          id: 'onb-1',
          timestamp: '2024-11-21T06:36:09.224354+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          data_visualization: {
            default_data_visualization: 'table',
            x_axis: 'Driver Name',
          },
          content:
            'The following analysis covers drivers with an average overall rating below 70 for the period from June 1st, 2026 to June 30th, 2026. A total of 12 drivers fall below the 70-point threshold and are listed below, ordered from lowest to highest rating.\n\n' +
            '```json\n\n' +
            '{"table": [{"Driver Name": "Lucas Auernhammer", "Total Rating": 23.0}, {"Driver Name": "Christian Scheunemann", "Total Rating": 25.0}, {"Driver Name": "Bruno Ranft", "Total Rating": 26.0}, {"Driver Name": "Ute Meyjohann", "Total Rating": 26.0}, {"Driver Name": "Erhard Brauer", "Total Rating": 28.0}, {"Driver Name": "Margit Br\\u00fcck", "Total Rating": 28.0}, {"Driver Name": "Torsten Mustermann", "Total Rating": 39.0}, {"Driver Name": "Marius Mehl", "Total Rating": 42.0}, {"Driver Name": "Thomas Mustermann", "Total Rating": 43.0}, {"Driver Name": "Bernd M\\u00fcller", "Total Rating": 44.0}, {"Driver Name": "Maik Fleischhauer", "Total Rating": 44.0}, {"Driver Name": "Sabine Lindenmann", "Total Rating": 48.0}]}\n```' +
            '\n\nSince you asked to identify drivers who need support, here is a breakdown by performance tier:\n\n' +
            '**Critical — Ratings below 30 (immediate attention recommended):**\n\n' +
            '- **Lucas Auernhammer** (23.0), **Christian Scheunemann** (25.0), **Bruno Ranft** (26.0), **Ute Meyjohann** (26.0), **Erhard Brauer** (28.0), and **Margit Brück** (28.0) are the lowest-rated drivers in the fleet this month. These drivers should be prioritized for individual coaching and targeted support as soon as possible.\n\n' +
            '**Moderate concern — Ratings between 30 and 50:**\n\n' +
            '- **Torsten Mustermann** (39.0), **Marius Mehl** (42.0), **Thomas Mustermann** (43.0), **Bernd Müller** (44.0), **Maik Fleischhauer** (44.0), and **Sabine Lindenmann** (48.0) are also below the 70-point threshold and would benefit from structured coaching to improve their overall performance.\n\n' +
            'To understand the root causes of these low ratings, it is recommended to review the individual sub-ratings (coasting, braking, cruise control, overspeed, and acceleration) for each driver. MAN ProfiDrive and the Connected CoDriver program offer targeted training and efficiency coaching that can help these drivers improve their scores.',
        },
      ],
    },
  },

  'step-2': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-2',
          timestamp: '2024-11-21T06:36:39.224360+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content: 'What is the reason for the low rating of Lucas Auernhammer?',
        },
        {
          id: 'onb-3',
          timestamp: '2024-11-21T06:37:09.224366+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          data_visualization: {
            default_data_visualization: 'table',
          },
          content:
            "Here is the detailed breakdown of Lucas Auernhammer's ratings for July 1st, 2026 to July 31st, 2026:\n\n" +
            '```json\n\n' +
            '{"table":[{"KPI":"Driver Name","":"Lucas Auernhammer"},{"KPI":"Total Rating","":23.0},{"KPI":"Coasting Rating","":20.0},{"KPI":"Acceleration Pedal Rating","":30.0},{"KPI":"Braking Behavior Rating","":15.0},{"KPI":"Cruise Control Usage Rating","":50.0},{"KPI":"Overspeed Rating","":0.0}]}\n' +
            '```\n\n' +
            '**Overspeed Rating (0.0):** This is the most critical issue.\n\n' +
            '**Braking Behavior Rating (15.0):** Indicates frequent harsh or abrupt braking events.\n\n' +
            '**Coasting Rating (20.0):** Shows insufficient use of vehicle momentum.\n\n' +
            '**Acceleration Pedal Rating (30.0):** Points to aggressive acceleration.\n\n' +
            '**Cruise Control Usage Rating (50.0):** Cruise control is not being used as frequently as recommended.\n\n' +
            'In summary, the primary reason for the low total rating is the combination of speeding, harsh braking, insufficient coasting, and aggressive acceleration.',
        },
      ],
    },
  },

  'step-3': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-4',
          timestamp: '2024-11-21T06:37:39.224372+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content: 'How is the brake rating put together?',
        },
        {
          id: 'onb-5',
          timestamp: '2024-11-21T06:38:09.224378+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          content:
            'The brake rating in MAN Perform is composed of two key factors:\n\n' +
            '**1. Service Brake Distance Ratio**\n\n' +
            '- The total distance travelled using the service brake is measured as a percentage of overall driving distance.\n\n' +
            '- A score of **100%** requires less than **1%** of the total distance to be braked using the service brake.\n\n' +
            '- A score of **0%** is given when more than **5.5%** of the distance is braked using the service brake.\n\n' +
            '**2. Wear-Free Brake vs. Service Brake Ratio**\n\n' +
            '- The ratio between wear-free braking (retarder and engine brake) and service brake usage is evaluated.\n\n' +
            '- A score of **100%** requires a ratio of **5:1 or more** (wear-free brake to service brake).\n\n' +
            '- A score of **0%** is given at a ratio of **0.5:1 or lower**.\n\n' +
            '**Recommendation for Improvement**\n\n' +
            'Whenever traffic conditions allow, drivers should use the **retarder or engine brake** instead of the service brake. Anticipatory driving — releasing the accelerator early and coasting before deceleration phases — naturally reduces reliance on the service brake and improves this rating.' +
            '\n\n1. [BA eTGS MFL 2026 Electric - Page 87 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/BA_eTGS_MFL_2026_Electric.pdf#page=87)\n\n' +
            '2. [BA eTGX MFL 2026 Electric - Page 79 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/BA_eTGX_MFL_2026_Electric.pdf#page=79)\n\n' +
            '3. [Perform Driver Review 1 - Page 3 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/Perform_Driver_Review_1.pdf#page=3)\n\n' +
            '4. [Perform Webinar 1 - Page 5 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/Perform_Webinar_1.pdf#page=5)\n\n' +
            '5. [2026 Perform Ratings - Page 5 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/2026_Perform_Ratings.pdf#page=5)',
        },
      ],
    },
  },

  'step-4': {
    messages: {
      role: 'ai',
      content: [
        {
          id: 'onb-6',
          timestamp: '2024-11-21T06:38:39.224384+00:00',
          role: 'human',
          threadId: ONBOARDING_THREAD_ID,
          content: 'What can my driver Lucas Auernhammer who has a low brake pedal rating do to improve?',
        },
        {
          id: 'onb-7',
          timestamp: '2024-11-21T06:39:09.224403+00:00',
          role: 'ai',
          threadId: ONBOARDING_THREAD_ID,
          data_visualization: {
            default_data_visualization: 'table',
          },
          content:
            'Here is what Lucas Auernhammer can do to improve his brake pedal rating:\n\n' +
            '**1. Prioritise the retarder or engine brake**\n\n' +
            'Whenever traffic allows, Lucas should use the retarder (stalk switch) before touching the service brake.\n\n' +
            '- The target ratio is **5:1 or better** — for every kilometre braked with the service brake, at least five kilometres should be braked using wear-free systems.\n\n' +
            '- Service brake use should stay **below 1% of total distance** for a perfect score.\n\n' +
            '**2. Drive more anticipatorily**\n\n' +
            '- Look further ahead to identify braking situations early — junctions, roundabouts, slower traffic, downhill sections.\n\n' +
            '- Lift off the accelerator sooner so speed bleeds off naturally before a braking point.\n\n' +
            '- Engage the retarder progressively and early, rather than reaching for the foot brake at the last moment.\n\n' +
            '**3. Use cruise control consistently on open roads**\n\n' +
            'Cruise control helps maintain a steady speed, reducing unnecessary acceleration and braking cycles.\n\n' +
            '**4. Track weekly progress**\n\n' +
            'Lucas can review his weekly driver report to compare his brake rate against the previous week and monitor improvement over time.\n\n' +
            '**Note:** Anticipatory driving improves both the braking and coasting ratings simultaneously — lifting off the throttle earlier means arriving at braking points at lower speed, reducing the need for any brake input.\n\n' +
            '\n\n\n\nBased on the following documents the answer was generated (ranked by relevance):\n\n' +
            '1. [Perform Webinar 1 - Page 5 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/Perform_Webinar_1.pdf#page=5)\n\n' +
            '2. [Perform Webinar 1 - Page 6 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/Perform_Webinar_1.pdf#page=6)\n\n' +
            '3. [Perform Driver Review 1 - Page 3 ](https://fleetassistant.rio.cloud/docs/language=EN/market=all/Perform_Driver_Review_1.pdf#page=3)',
        },
      ],
    },
  },
};
